// Módulo Estoque — Fase 1
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import estoqueService from "../../services/estoqueService";
import pedidosService from "../../services/pedidosService";
import fornecedoresService from "../../services/fornecedoresService";
import Banner from "../../components/ui/Banner";
import SaldoDuplo from "../../components/estoque/SaldoDuplo";
import ItemEstoqueAutocomplete from "../../components/estoque/ItemEstoqueAutocomplete";
import {
  itemIdUmBase, itemIdUmOp, itemIdUmCompra, itemSiglaBase,
  unidadesBase, unidadesOperacionais, calcQtdBase,
  aplicarSugestaoCategoria, SUGESTAO_UM_CATEGORIA,
} from "../../utils/estoqueUm";

const fmtQtd = (v, dec = 3) => Number(v || 0).toLocaleString("pt-BR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: dec,
});
const fmtMoeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtD = (d) => {
  try {
    const s = (d || "").split("T")[0].split(" ")[0];
    return s ? new Date(`${s}T12:00:00`).toLocaleDateString("pt-BR") : "—";
  } catch { return d || "—"; }
};

const CAT_LABEL = {
  Limpeza: "Limpeza", Manutencao: "Manutenção", Piscina: "Piscina",
  Eletrica: "Elétrica", EPI: "EPI", Escritorio: "Escritório", Outro: "Outro",
};
const MOV_LABEL = { Entrada: "Entrada", Saida: "Saída", Transferencia: "Transferência" };

const localNome = (l) => l?.nmLocal || l?.nmlocal || "";
const localDescricao = (l) => String(l?.descricao ?? "").trim();

const FAMILIA_LABEL = {
  Massa: "Massa", Comprimento: "Comprimento", Volume: "Volume", Unidade: "Unidade", Outro: "Outro",
};

const ITEM_EMPTY = {
  cdItem: "", nmItem: "", categoria: "Outro",
  idUnidadeBase: "", idUnidadeOperacional: "", idUnidadeCompra: "",
  estoqueMinimo: "0", idLocalPadrao: "", idFornecedor: "", observacao: "", ativo: 1,
};
const KIT_LINHA_EMPTY = { idItem: "", quantidade: "", idUnidadeInformada: "" };
const KIT_EMPTY = {
  nmKit: "", categoria: "Piscina", observacao: "", ativo: 1,
  linhas: [{ ...KIT_LINHA_EMPTY }],
};
const LOCAL_EMPTY = { nmLocal: "", descricao: "", ativo: 1 };
const UM_EMPTY = {
  sigla: "", descricao: "", familia: "Outro", idUnidadeBase: "", fatorParaBase: "1", ativo: 1,
};
const MOV_EMPTY = {
  tpMovimento: "Entrada", idItem: "", idLocalOrigem: "", idLocalDestino: "",
  quantidadeInformada: "", idUnidadeInformada: "",
  dtMovimento: new Date().toISOString().split("T")[0],
  documentoRef: "", observacao: "", vlrUnitario: "", vlrTotal: "",
};

export default function EstoqueView({ onNavigate }) {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("estoque");
  const canPedidos = podeEditar("pedidos");
  const [idCondo, setIdCondo] = useState("");
  const [aba, setAba] = useState("resumo");
  const [meta, setMeta] = useState({ categorias: [], unidades: [], familias: [], tpMovimento: [] });
  const [unidades, setUnidades] = useState([]);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  const [resumo, setResumo] = useState({ totalItensAtivos: 0, itensAbaixoMinimo: 0, ultimasMovimentacoes: [] });
  const [abaixo, setAbaixo] = useState([]);

  const [locais, setLocais] = useState([]);
  const [itens, setItens] = useState([]);
  const [itensPag, setItensPag] = useState({ total: 0, totalPages: 1 });
  const [movs, setMovs] = useState([]);
  const [movsPag, setMovsPag] = useState({ total: 0, totalPages: 1 });

  const [searchItem, setSearchItem] = useState("");
  const [filtroCat, setFiltroCat] = useState("");
  const [filtroAbaixo, setFiltroAbaixo] = useState("");
  const [pageItem, setPageItem] = useState(1);
  const [pageMov, setPageMov] = useState(1);

  const [showItem, setShowItem] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formItem, setFormItem] = useState(ITEM_EMPTY);
  const [showLocal, setShowLocal] = useState(false);
  const [editLocal, setEditLocal] = useState(null);
  const [formLocal, setFormLocal] = useState(LOCAL_EMPTY);
  const [showMov, setShowMov] = useState(false);
  const [formMov, setFormMov] = useState(MOV_EMPTY);
  const [showUm, setShowUm] = useState(false);
  const [editUm, setEditUm] = useState(null);
  const [formUm, setFormUm] = useState(UM_EMPTY);
  const [fornecedores, setFornecedores] = useState([]);
  const [itensSelect, setItensSelect] = useState([]);
  const [saving, setSaving] = useState(false);
  const [diasCobertura, setDiasCobertura] = useState("15");
  const [diasHistorico, setDiasHistorico] = useState("90");
  const [reposicao, setReposicao] = useState({ linhas: [], parametros: {} });
  const [loadingReposicao, setLoadingReposicao] = useState(false);
  const [exportandoCsv, setExportandoCsv] = useState(false);
  const [kits, setKits] = useState([]);
  const [showKit, setShowKit] = useState(false);
  const [editKit, setEditKit] = useState(null);
  const [formKit, setFormKit] = useState(KIT_EMPTY);
  const [filtroKitCat, setFiltroKitCat] = useState("");
  const [showAplicarKit, setShowAplicarKit] = useState(false);
  const [kitAplicar, setKitAplicar] = useState(null);
  const [formAplicarKit, setFormAplicarKit] = useState({ idLocalOrigem: "", observacao: "", linhas: [] });

  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  const carregarUnidades = useCallback(async () => {
    if (!idCondo) { setUnidades([]); return; }
    const r = await estoqueService.listarUnidadesMedida({ idCondominio: idCondo });
    setUnidades(r.data || []);
    setMeta((m) => ({ ...m, unidades: r.data || [], familias: r.familias || m.familias }));
  }, [idCondo]);

  useEffect(() => {
    if (!idCondo) return;
    estoqueService.meta({ idCondominio: idCondo }).then((r) => setMeta(r.data || {})).catch(() => {});
    carregarUnidades();
    fornecedoresService.listar({ idCondominio: idCondo, situacao: "ativo", limit: 200 })
      .then((r) => setFornecedores(r.data || [])).catch(() => {});
    estoqueService.listarItens({ idCondominio: idCondo, ativo: "1", limit: 500 })
      .then((r) => setItensSelect(r.data || [])).catch(() => {});
  }, [idCondo, carregarUnidades]);

  const carregarLocais = useCallback(async () => {
    if (!idCondo) { setLocais([]); return; }
    const r = await estoqueService.listarLocais({ idCondominio: idCondo });
    setLocais(r.data || []);
  }, [idCondo]);

  const carregarResumo = useCallback(async () => {
    if (!idCondo) return;
    const [rs, ab] = await Promise.all([
      estoqueService.resumo(idCondo),
      estoqueService.abaixoMinimo(idCondo),
    ]);
    setResumo(rs.data || {});
    setAbaixo(ab.data || []);
  }, [idCondo]);

  const carregarItens = useCallback(async () => {
    if (!idCondo) { setItens([]); return; }
    const r = await estoqueService.listarItens({
      idCondominio: idCondo, search: searchItem, categoria: filtroCat,
      abaixoMinimo: filtroAbaixo, page: pageItem, limit: 15,
    });
    setItens(r.data || []);
    setItensPag(r.pagination || { total: 0, totalPages: 1 });
  }, [idCondo, searchItem, filtroCat, filtroAbaixo, pageItem]);

  const carregarMovs = useCallback(async () => {
    if (!idCondo) { setMovs([]); return; }
    const r = await estoqueService.listarMovimentacoes({
      idCondominio: idCondo, page: pageMov, limit: 20,
    });
    setMovs(r.data || []);
    setMovsPag(r.pagination || { total: 0, totalPages: 1 });
  }, [idCondo, pageMov]);

  const carregarKits = useCallback(async () => {
    if (!idCondo) { setKits([]); return; }
    const r = await estoqueService.listarKits({
      idCondominio: idCondo, categoria: filtroKitCat, ativo: "1",
    });
    setKits(r.data || []);
  }, [idCondo, filtroKitCat]);

  const carregarReposicao = useCallback(async () => {
    if (!idCondo) return;
    setLoadingReposicao(true);
    try {
      const r = await estoqueService.relatorioReposicao({
        idCondominio: idCondo,
        diasCobertura,
        diasHistorico,
        somenteNecessarios: "1",
      });
      setReposicao(r.data || { linhas: [] });
    } catch (e) { setError(e.message); }
    finally { setLoadingReposicao(false); }
  }, [idCondo, diasCobertura, diasHistorico]);

  const refresh = useCallback(async () => {
    if (!idCondo) return;
    setError(null);
    try {
      await carregarLocais();
      if (aba === "resumo") await carregarResumo();
      if (aba === "itens") await carregarItens();
      if (aba === "movimentacoes") await carregarMovs();
      if (aba === "reposicao") await carregarReposicao();
      if (aba === "locais") { /* locais já */ }
      if (aba === "unidades") await carregarUnidades();
      if (aba === "kits") await carregarKits();
    } catch (e) {
      setError(e.message);
    }
  }, [idCondo, aba, carregarLocais, carregarResumo, carregarItens, carregarMovs, carregarReposicao, carregarUnidades, carregarKits]);

  useEffect(() => { refresh(); }, [refresh]);

  const exportarReposicaoCsv = async () => {
    if (!idCondo) return;
    setExportandoCsv(true);
    try {
      await estoqueService.exportReposicaoCsv({
        idCondominio: idCondo, diasCobertura, diasHistorico, somenteNecessarios: "1",
      });
      setOkMsg("CSV de reposição exportado.");
    } catch (e) { setError(e.message); }
    finally { setExportandoCsv(false); }
  };

  const exportarPosicaoCsv = async () => {
    if (!idCondo) return;
    setExportandoCsv(true);
    try {
      await estoqueService.exportPosicaoCsv({ idCondominio: idCondo, ativo: "1" });
      setOkMsg("CSV de posição de estoque exportado.");
    } catch (e) { setError(e.message); }
    finally { setExportandoCsv(false); }
  };

  const gerarPedidosReposicao = async () => {
    if (!canPedidos) {
      setError("Sem permissão Total em Pedidos para gerar.");
      return;
    }
    setSaving(true); setError(null);
    try {
      const r = await pedidosService.gerarReposicao({
        idCondominio: idCondo,
        diasCobertura: parseInt(diasCobertura, 10) || 0,
        diasHistorico: parseInt(diasHistorico, 10) || 90,
        agruparFornecedor: true,
      });
      setOkMsg(r.message || "Pedido(s) criado(s).");
      if (onNavigate) onNavigate("pedidos");
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const salvarItem = async () => {
    if (!canEdit || !idCondo) return;
    setSaving(true); setError(null);
    try {
      const payload = {
        ...formItem, idCondominio: idCondo,
        estoqueMinimo: parseFloat(formItem.estoqueMinimo || 0),
        idLocalPadrao: formItem.idLocalPadrao || null,
        idFornecedor: formItem.idFornecedor || null,
        idUnidadeCompra: formItem.idUnidadeCompra || null,
        idUnidadeOperacional: formItem.idUnidadeOperacional || null,
      };
      if (editItem?.id) await estoqueService.atualizarItem(editItem.id, payload);
      else await estoqueService.criarItem(payload);
      setShowItem(false);
      setOkMsg(editItem ? "Item atualizado." : "Item cadastrado.");
      await carregarItens();
      const rSel = await estoqueService.listarItens({ idCondominio: idCondo, ativo: "1", limit: 500 });
      setItensSelect(rSel.data || []);
      if (aba === "resumo") await carregarResumo();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const salvarLocal = async () => {
    if (!canEdit || !idCondo) return;
    setSaving(true); setError(null);
    try {
      const payload = { ...formLocal, idCondominio: idCondo };
      if (editLocal?.id) await estoqueService.atualizarLocal(editLocal.id, payload);
      else await estoqueService.criarLocal(payload);
      setShowLocal(false);
      setOkMsg(editLocal ? "Local atualizado." : "Local cadastrado.");
      await carregarLocais();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const salvarUm = async () => {
    if (!canEdit || !idCondo) return;
    setSaving(true); setError(null);
    try {
      const payload = {
        idCondominio: idCondo,
        sigla: formUm.sigla,
        descricao: formUm.descricao,
        familia: formUm.familia,
        idUnidadeBase: formUm.idUnidadeBase || null,
        fatorParaBase: parseFloat(formUm.fatorParaBase) || 1,
        ativo: formUm.ativo ? 1 : 0,
      };
      if (editUm?.id) await estoqueService.atualizarUnidadeMedida(editUm.id, payload);
      else await estoqueService.criarUnidadeMedida(payload);
      setShowUm(false);
      setOkMsg(editUm ? "Unidade atualizada." : "Unidade cadastrada.");
      await carregarUnidades();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const kitLinhasValidas = (linhas) => linhas.filter(
    (l) => l.idItem && (parseFloat(l.quantidade) || 0) > 0
  );

  const abrirKit = async (row = null) => {
    setError(null);
    if (row?.id) {
      setEditKit(row);
      try {
        const r = await estoqueService.buscarKit(row.id);
        const k = r.kit || {};
        setFormKit({
          nmKit: k.nmkit || k.nmKit || "",
          categoria: k.categoria || "Piscina",
          observacao: k.observacao || "",
          ativo: k.ativo ?? 1,
          linhas: (r.linhas || []).length
            ? (r.linhas || []).map((l) => ({
              idItem: String(l.iditem || l.idItem || ""),
              quantidade: String(l.quantidade ?? ""),
              idUnidadeInformada: String(l.idunidadeinformada || l.idUnidadeInformada || ""),
            }))
            : [{ ...KIT_LINHA_EMPTY }],
        });
      } catch (e) { setError(e.message); return; }
    } else {
      setEditKit(null);
      setFormKit({ ...KIT_EMPTY, linhas: [{ ...KIT_LINHA_EMPTY }] });
    }
    setShowKit(true);
  };

  const updLinhaKit = (idx, patch) => {
    setFormKit((p) => ({
      ...p,
      linhas: p.linhas.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };

  const salvarKit = async () => {
    if (!canEdit || !idCondo) return;
    const linhas = kitLinhasValidas(formKit.linhas);
    if (!formKit.nmKit.trim() || !linhas.length) return;
    setSaving(true); setError(null);
    try {
      const payload = {
        idCondominio: idCondo,
        nmKit: formKit.nmKit.trim(),
        categoria: formKit.categoria,
        observacao: formKit.observacao,
        ativo: formKit.ativo ? 1 : 0,
        linhas: linhas.map((l, idx) => ({
          idItem: l.idItem,
          quantidade: parseFloat(l.quantidade) || 0,
          idUnidadeInformada: l.idUnidadeInformada || null,
          ordem: idx + 1,
        })),
      };
      if (editKit?.id) await estoqueService.atualizarKit(editKit.id, payload);
      else await estoqueService.criarKit(payload);
      setShowKit(false);
      setOkMsg(editKit ? "Kit atualizado." : "Kit cadastrado.");
      await carregarKits();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const abrirAplicarKit = async (kit) => {
    if (!canEdit) return;
    setSaving(true); setError(null);
    try {
      const r = await estoqueService.buscarKit(kit.id);
      const linhas = (r.linhas || []).map((l) => ({
        id: l.id,
        idItem: String(l.iditem || l.idItem || ""),
        quantidade: String(l.quantidade ?? ""),
        idUnidadeInformada: String(l.idunidadeinformada || l.idUnidadeInformada || ""),
        nmitem: l.nmitem,
        cditem: l.cditem,
      }));
      if (!linhas.length) {
        setError("Kit sem itens.");
        return;
      }
      const idLocal = String(locais.find((l) => l.ativo)?.id || "");
      setKitAplicar(r.kit || kit);
      setFormAplicarKit({ idLocalOrigem: idLocal, observacao: "", linhas });
      setShowAplicarKit(true);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const updLinhaAplicar = (idx, patch) => {
    setFormAplicarKit((p) => ({
      ...p,
      linhas: p.linhas.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };

  const confirmarAplicarKit = async () => {
    if (!canEdit || !kitAplicar?.id) return;
    if (!formAplicarKit.idLocalOrigem) {
      setError("Selecione o local de saída.");
      return;
    }
    const nm = kitAplicar.nmkit || kitAplicar.nmKit || "kit";
    if (!window.confirm(`Aplicar "${nm}" e registrar ${formAplicarKit.linhas.length} saída(s)?`)) return;
    setSaving(true); setError(null);
    try {
      const r = await estoqueService.aplicarKit(kitAplicar.id, {
        idLocalOrigem: formAplicarKit.idLocalOrigem,
        observacao: formAplicarKit.observacao || undefined,
        linhas: formAplicarKit.linhas.map((l) => ({
          idLinha: l.id,
          idItem: l.idItem,
          quantidade: parseFloat(l.quantidade) || 0,
          idUnidadeInformada: l.idUnidadeInformada || null,
        })),
      });
      setShowAplicarKit(false);
      setOkMsg(r.message || `Kit "${nm}" aplicado.`);
      if (aba === "resumo") await carregarResumo();
      if (aba === "movimentacoes") await carregarMovs();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const abrirUm = (row = null) => {
    if (row) {
      setEditUm(row);
      setFormUm({
        sigla: row.sigla || "",
        descricao: row.descricao || "",
        familia: row.familia || "Outro",
        idUnidadeBase: row.idUnidadeBase || "",
        fatorParaBase: String(row.fatorParaBase ?? 1),
        ativo: row.ativo ?? 1,
      });
    } else {
      setEditUm(null);
      setFormUm(UM_EMPTY);
    }
    setShowUm(true);
  };

  const umMovSelecionada = unidades.find((u) => String(u.id) === String(formMov.idUnidadeInformada));
  const qtdBasePreview = calcQtdBase(formMov.quantidadeInformada, umMovSelecionada);
  const siglaBasePreview = umMovSelecionada?.siglaBase || umMovSelecionada?.sigla || itemSiglaBase(
    itensSelect.find((i) => String(i.id) === String(formMov.idItem))
  );

  const salvarMov = async () => {
    if (!canEdit || !idCondo) return;
    setSaving(true); setError(null);
    try {
      await estoqueService.criarMovimentacao({
        idCondominio: idCondo,
        tpMovimento: formMov.tpMovimento,
        idItem: formMov.idItem,
        idLocalOrigem: formMov.idLocalOrigem,
        idLocalDestino: formMov.idLocalDestino,
        dtMovimento: formMov.dtMovimento,
        documentoRef: formMov.documentoRef,
        observacao: formMov.observacao,
        quantidadeInformada: parseFloat(formMov.quantidadeInformada),
        idUnidadeInformada: formMov.idUnidadeInformada,
        vlrUnitario: formMov.vlrUnitario !== "" ? parseFloat(formMov.vlrUnitario) : null,
        vlrTotal: formMov.vlrTotal !== "" ? parseFloat(formMov.vlrTotal) : null,
      });
      setShowMov(false);
      setFormMov({ ...MOV_EMPTY, dtMovimento: new Date().toISOString().split("T")[0] });
      setOkMsg("Movimentação registrada.");
      await carregarMovs();
      await carregarItens();
      if (aba === "resumo") await carregarResumo();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const abrirItem = (row = null) => {
    if (row) {
      setEditItem(row);
      setFormItem({
        cdItem: row.cdItem || "", nmItem: row.nmItem || "", categoria: row.categoria || "Outro",
        idUnidadeBase: itemIdUmBase(row) || "",
        idUnidadeOperacional: itemIdUmOp(row) || "",
        idUnidadeCompra: itemIdUmCompra(row) || "",
        estoqueMinimo: String(row.estoqueMinimo ?? 0),
        idLocalPadrao: row.idLocalPadrao || "", idFornecedor: row.idFornecedor || "",
        observacao: row.observacao || "", ativo: row.ativo ?? 1,
      });
    } else {
      setEditItem(null);
      const idUn = unidadesBase(unidades).find((u) => u.sigla === "UN")?.id || unidadesBase(unidades)[0]?.id || "";
      setFormItem({ ...ITEM_EMPTY, idUnidadeBase: idUn });
    }
    setShowItem(true);
  };

  const aoSelecionarItemMov = (idItem) => {
    const it = itensSelect.find((i) => String(i.id) === String(idItem));
    let idUm = itemIdUmBase(it) || "";
    if (formMov.tpMovimento === "Saida") {
      const op = itemIdUmOp(it);
      if (op) idUm = op;
    }
    setFormMov((p) => ({ ...p, idItem, idUnidadeInformada: idUm, quantidadeInformada: "" }));
  };

  const abrirLocal = (row = null) => {
    if (row) {
      setEditLocal(row);
      setFormLocal({
        nmLocal: localNome(row), descricao: localDescricao(row), ativo: row.ativo ?? 1,
      });
    } else {
      setEditLocal(null);
      setFormLocal(LOCAL_EMPTY);
    }
    setShowLocal(true);
  };

  if (!idCondo) {
    return (
      <div className="page-header">
        <h1 className="page-title">Estoque</h1>
        <p className="page-subtitle">Selecione um condomínio no topo para gerenciar o almoxarifado.</p>
      </div>
    );
  }

  const tabs = [
    { id: "resumo", label: "Resumo", icon: "bi-speedometer2" },
    { id: "reposicao", label: "Reposição", icon: "bi-clipboard-data" },
    { id: "itens", label: "Itens", icon: "bi-box" },
    { id: "unidades", label: "Unidades", icon: "bi-rulers" },
    { id: "kits", label: "Kits", icon: "bi-box-seam" },
    { id: "locais", label: "Locais", icon: "bi-geo-alt" },
    { id: "movimentacoes", label: "Movimentações", icon: "bi-arrow-left-right" },
  ];

  return (
    <div>
      <div className="page-header d-flex flex-wrap align-items-start justify-content-between gap-2">
        <div>
          <h1 className="page-title">Estoque</h1>
          <p className="page-subtitle">
            Almoxarifado — {condominioAtivo?.nmFantasia || condominioAtivo?.nmCondominio || "Condomínio"}
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setFormMov({ ...MOV_EMPTY, dtMovimento: new Date().toISOString().split("T")[0] }); setShowMov(true); }}>
            <i className="bi bi-plus-lg me-1" /> Nova movimentação
          </button>
        )}
      </div>

      {error && <Banner tipo="danger" onClose={() => setError(null)}>{error}</Banner>}
      {okMsg && <Banner tipo="success" onClose={() => setOkMsg(null)}>{okMsg}</Banner>}

      <ul className="nav nav-tabs mb-3">
        {tabs.map((t) => (
          <li className="nav-item" key={t.id}>
            <button type="button" className={`nav-link ${aba === t.id ? "active" : ""}`} onClick={() => setAba(t.id)}>
              <i className={`bi ${t.icon} me-1`} />{t.label}
            </button>
          </li>
        ))}
      </ul>

      {aba === "resumo" && (
        <div>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="text-muted small">Itens ativos</div>
                  <div className="fs-3 fw-bold">{resumo.totalItensAtivos ?? 0}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #F59E0B" }}>
                <div className="card-body">
                  <div className="text-muted small">Abaixo do mínimo</div>
                  <div className="fs-3 fw-bold text-warning">{resumo.itensAbaixoMinimo ?? 0}</div>
                </div>
              </div>
            </div>
          </div>
          {abaixo.length > 0 && (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-transparent fw-semibold">Itens em alerta</div>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead><tr><th>Código</th><th>Item</th><th>Saldo</th><th>Mínimo</th></tr></thead>
                  <tbody>
                    {abaixo.map((r) => (
                      <tr key={r.id}>
                        <td>{r.cdItem}</td>
                        <td>{r.nmItem}</td>
                        <td className="text-danger fw-semibold">
                          <SaldoDuplo qtd={r.saldoAtual} unidades={unidades} row={r} />
                        </td>
                        <td>
                          <SaldoDuplo qtd={r.estoqueMinimo} unidades={unidades} row={r} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent fw-semibold">Últimas movimentações</div>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr><th>Data</th><th>Tipo</th><th>Item</th><th>Local</th><th className="text-end">Qtd</th></tr>
                </thead>
                <tbody>
                  {(resumo.ultimasMovimentacoes || []).map((m) => (
                    <tr key={m.id}>
                      <td>{fmtD(m.dtMovimento)}</td>
                      <td>{MOV_LABEL[m.tpMovimento] || m.tpMovimento}</td>
                      <td>{m.nmItem}</td>
                      <td>{m.nmLocalOrigem}</td>
                      <td className="text-end">{fmtQtd(m.quantidade)}</td>
                    </tr>
                  ))}
                  {!(resumo.ultimasMovimentacoes || []).length && (
                    <tr><td colSpan={5} className="text-center text-muted py-3">Nenhuma movimentação ainda.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {aba === "reposicao" && (
        <div>
          <div className="card border-0 shadow-sm p-3 mb-3">
            <p className="small text-muted mb-2">
              Quantidade sugerida = estoque mínimo + (consumo médio diário × dias de cobertura) − saldo atual.
              Consumo calculado pelas <strong>saídas</strong> no período informado.
            </p>
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-0">Cobertura (dias)</label>
                <input type="number" min="0" className="form-control form-control-sm" style={{ width: 100 }}
                  value={diasCobertura} onChange={(e) => setDiasCobertura(e.target.value)} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-0">Histórico (dias)</label>
                <input type="number" min="1" max="365" className="form-control form-control-sm" style={{ width: 100 }}
                  value={diasHistorico} onChange={(e) => setDiasHistorico(e.target.value)} />
              </div>
              <div className="col-auto">
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={loadingReposicao}
                  onClick={carregarReposicao}>
                  {loadingReposicao ? "Calculando…" : "Atualizar"}
                </button>
              </div>
              {canPedidos && (
                <div className="col-auto">
                  <button type="button" className="btn btn-sm btn-primary" disabled={saving || !reposicao.linhas?.length}
                    onClick={gerarPedidosReposicao}>
                    <i className="bi bi-cart-check me-1" /> Gerar pedido(s)
                  </button>
                </div>
              )}
              <div className="col-auto">
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={exportandoCsv}
                  onClick={exportarReposicaoCsv} title="Exportar CSV">
                  <i className="bi bi-filetype-csv me-1" /> CSV
                </button>
              </div>
            </div>
          </div>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>Código</th><th>Item</th><th className="text-end">Saldo</th>
                    <th className="text-end">Mín.</th><th className="text-end">Cons./dia</th>
                    <th className="text-end">Alvo</th><th className="text-end">Sugerido</th>
                    <th>Fornecedor</th>
                  </tr>
                </thead>
                <tbody>
                  {(reposicao.linhas || []).map((l) => (
                    <tr key={l.idItem} className={l.abaixoMinimo ? "table-warning" : ""}>
                      <td><code>{l.cdItem}</code></td>
                      <td>{l.nmItem}</td>
                      <td className="text-end">
                        <SaldoDuplo qtd={l.saldoAtual} unidades={unidades} row={l} className="d-inline-block text-end" />
                      </td>
                      <td className="text-end">
                        <SaldoDuplo qtd={l.estoqueMinimo} unidades={unidades} row={l} className="d-inline-block text-end" />
                      </td>
                      <td className="text-end">{fmtQtd(l.consumoMedioDia, 4)} {itemSiglaBase(l)}/dia</td>
                      <td className="text-end">{fmtQtd(l.estoqueAlvo)} {itemSiglaBase(l)}</td>
                      <td className="text-end fw-semibold">
                        <SaldoDuplo qtd={l.qtdSugerida} unidades={unidades} row={l} className="d-inline-block text-end" />
                      </td>
                      <td className="small">{l.nmFornecedor || "—"}</td>
                    </tr>
                  ))}
                  {!reposicao.linhas?.length && !loadingReposicao && (
                    <tr><td colSpan={8} className="text-center text-muted py-3">Nenhum item para reposição com os filtros atuais.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {aba === "itens" && (
        <div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <input className="form-control form-control-sm" style={{ maxWidth: 220 }} placeholder="Buscar item..."
              value={searchItem} onChange={(e) => { setSearchItem(e.target.value); setPageItem(1); }} />
            <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={filtroCat}
              onChange={(e) => { setFiltroCat(e.target.value); setPageItem(1); }}>
              <option value="">Todas categorias</option>
              {(meta.categorias || []).map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ maxWidth: 180 }} value={filtroAbaixo}
              onChange={(e) => { setFiltroAbaixo(e.target.value); setPageItem(1); }}>
              <option value="">Todos os saldos</option>
              <option value="1">Somente abaixo do mínimo</option>
            </select>
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={exportandoCsv}
              onClick={exportarPosicaoCsv} title="Exportar posição (CSV)">
              <i className="bi bi-filetype-csv me-1" /> Posição CSV
            </button>
            {canEdit && (
              <button type="button" className="btn btn-primary btn-sm ms-auto" onClick={() => abrirItem()}>
                <i className="bi bi-plus-lg me-1" /> Novo item
              </button>
            )}
          </div>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Código</th><th>Item</th><th>Categoria</th><th>Un.</th>
                    <th className="text-end">Saldo</th><th className="text-end">Mín.</th>
                    <th className="text-end">Custo médio</th><th className="text-end">Valor estoque</th><th />
                  </tr>
                </thead>
                <tbody>
                  {itens.map((r) => (
                    <tr key={r.id} className={Number(r.abaixoMinimo) ? "table-warning" : ""}>
                      <td><code>{r.cdItem}</code></td>
                      <td>{r.nmItem}</td>
                      <td>{CAT_LABEL[r.categoria] || r.categoria}</td>
                      <td>{itemSiglaBase(r)}{itemIdUmOp(r) ? ` / ${unidades.find((u) => String(u.id) === String(itemIdUmOp(r)))?.sigla || "op."}` : ""}</td>
                      <td className="text-end fw-semibold">
                        <SaldoDuplo qtd={r.saldoAtual} unidades={unidades} row={r} className="d-inline-block text-end" />
                      </td>
                      <td className="text-end">
                        <SaldoDuplo qtd={r.estoqueMinimo} unidades={unidades} row={r} className="d-inline-block text-end" />
                      </td>
                      <td className="text-end">
                        {fmtMoeda(r.custoMedio ?? r.customedio)}
                        <span className="text-muted small"> /{itemSiglaBase(r)}</span>
                      </td>
                      <td className="text-end">{fmtMoeda(r.valorEstoque ?? r.valorestoque)}</td>
                      <td className="text-end">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => abrirItem(r)}>
                          <i className="bi bi-pencil" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!itens.length && (
                    <tr><td colSpan={9} className="text-center text-muted py-4">Nenhum item cadastrado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {itensPag.totalPages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={pageItem <= 1}
                onClick={() => setPageItem((p) => p - 1)}>Anterior</button>
              <span className="align-self-center small text-muted">{pageItem} / {itensPag.totalPages}</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={pageItem >= itensPag.totalPages}
                onClick={() => setPageItem((p) => p + 1)}>Próxima</button>
            </div>
          )}
        </div>
      )}

      {aba === "locais" && (
        <div>
          <div className="d-flex mb-3">
            {canEdit && (
              <button type="button" className="btn btn-primary btn-sm ms-auto" onClick={() => abrirLocal()}>
                <i className="bi bi-plus-lg me-1" /> Novo local
              </button>
            )}
          </div>
          <div className="row g-3">
            {locais.map((l) => (
              <div className="col-md-6 col-lg-4" key={l.id}>
                <div className={`card border-0 shadow-sm h-100 ${!l.ativo ? "opacity-75" : ""}`}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between">
                      <h6 className="mb-1">{localNome(l)}</h6>
                      {!l.ativo && <span className="badge bg-secondary">Inativo</span>}
                    </div>
                    {localDescricao(l) && (
                      <p className="small text-muted mb-2">{localDescricao(l)}</p>
                    )}
                    {canEdit && (
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => abrirLocal(l)}>
                        Editar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!locais.length && <p className="text-muted">Cadastre o primeiro local (ex.: Almoxarifado).</p>}
          </div>
        </div>
      )}

      {aba === "unidades" && (
        <div>
          <div className="d-flex mb-3">
            <p className="small text-muted mb-0 me-auto align-self-center">
              Unidade <strong>base</strong> (fator 1) para estoque e custo médio; derivadas convertem na saída.
            </p>
            {canEdit && (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => abrirUm()}>
                <i className="bi bi-plus-lg me-1" /> Nova unidade
              </button>
            )}
          </div>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Sigla</th><th>Descrição</th><th>Família</th><th>Base</th>
                    <th className="text-end">Fator → base</th><th>Status</th><th />
                  </tr>
                </thead>
                <tbody>
                  {unidades.map((u) => (
                    <tr key={u.id} className={!u.ativo ? "text-muted" : ""}>
                      <td><code>{u.sigla}</code></td>
                      <td>{u.descricao}</td>
                      <td>{FAMILIA_LABEL[u.familia] || u.familia}</td>
                      <td>{u.ehBase ? "— (raiz)" : (u.siglaBase || "—")}</td>
                      <td className="text-end font-monospace">{u.fatorParaBase}</td>
                      <td>{u.ativo ? <span className="badge bg-success">Ativo</span> : <span className="badge bg-secondary">Inativo</span>}</td>
                      <td className="text-end">
                        {canEdit && (
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => abrirUm(u)}>
                            Editar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!unidades.length && (
                    <tr><td colSpan={7} className="text-center text-muted py-4">Carregando unidades…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {aba === "kits" && (
        <div>
          <p className="small text-muted mb-2">
            Kits agrupam vários itens (ex.: limpeza de piscina). Ao aplicar, o sistema registra uma saída por item, com conversão de UM e validação de saldo para o kit inteiro.
          </p>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <select className="form-select form-select-sm" style={{ maxWidth: 180 }}
              value={filtroKitCat} onChange={(e) => setFiltroKitCat(e.target.value)}>
              <option value="">Todas categorias</option>
              {(meta.categorias || []).map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
            </select>
            {canEdit && (
              <button type="button" className="btn btn-primary btn-sm ms-auto" onClick={() => abrirKit()}>
                <i className="bi bi-plus-lg me-1" /> Novo kit
              </button>
            )}
          </div>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nome</th><th>Categoria</th>
                    <th className="text-end">Itens</th><th />
                  </tr>
                </thead>
                <tbody>
                  {kits.map((k) => (
                    <tr key={k.id}>
                      <td>{k.nmkit || k.nmKit}</td>
                      <td>{CAT_LABEL[k.categoria] || k.categoria}</td>
                      <td className="text-end">{k.qtditens ?? "—"}</td>
                      <td className="text-end text-nowrap">
                        {canEdit && (
                          <>
                            <button type="button" className="btn btn-sm btn-success me-1"
                              onClick={() => abrirAplicarKit(k)} title="Aplicar kit">
                              Aplicar
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary"
                              onClick={() => abrirKit(k)}>
                              <i className="bi bi-pencil" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!kits.length && (
                    <tr><td colSpan={4} className="text-center text-muted py-4">Nenhum kit cadastrado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {aba === "movimentacoes" && (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Data</th><th>Tipo</th><th>Item</th><th>Origem</th><th>Destino</th>
                  <th className="text-end">Qtd</th><th>Ref.</th>
                </tr>
              </thead>
              <tbody>
                {movs.map((m) => (
                  <tr key={m.id}>
                    <td>{fmtD(m.dtMovimento)}</td>
                    <td><span className="badge bg-light text-dark">{MOV_LABEL[m.tpMovimento] || m.tpMovimento}</span></td>
                    <td>{m.nmItem}</td>
                    <td>{m.nmLocalOrigem}</td>
                    <td>{m.nmLocalDestino || "—"}</td>
                    <td className="text-end">
                      {m.quantidadeInformada != null && (m.siglaUnidadeInformada || m.siglaunidadeinformada) ? (
                        <>
                          {fmtQtd(m.quantidadeInformada)} {m.siglaUnidadeInformada || m.siglaunidadeinformada}
                          <div className="small text-muted">= {fmtQtd(m.quantidade)} {m.unidadeMedida || m.unidademedida}</div>
                        </>
                      ) : (
                        <>{fmtQtd(m.quantidade)} {m.unidadeMedida || m.unidademedida}</>
                      )}
                    </td>
                    <td className="small text-muted">{m.documentoRef || "—"}</td>
                  </tr>
                ))}
                {!movs.length && (
                  <tr><td colSpan={7} className="text-center text-muted py-4">Nenhuma movimentação.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {movsPag.totalPages > 1 && (
            <div className="d-flex justify-content-center gap-2 p-3">
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={pageMov <= 1}
                onClick={() => setPageMov((p) => p - 1)}>Anterior</button>
              <span className="align-self-center small text-muted">{pageMov} / {movsPag.totalPages}</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={pageMov >= movsPag.totalPages}
                onClick={() => setPageMov((p) => p + 1)}>Próxima</button>
            </div>
          )}
        </div>
      )}

      {/* Modal Item */}
      {showItem && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editItem ? "Editar item" : "Novo item"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowItem(false)} />
              </div>
              <div className="modal-body row g-2">
                <div className="col-md-4">
                  <label className="form-label small">Código</label>
                  <input className="form-control form-control-sm" value={formItem.cdItem}
                    onChange={(e) => setFormItem((p) => ({ ...p, cdItem: e.target.value }))}
                    placeholder="Auto se vazio" disabled={!!editItem && !canEdit} />
                </div>
                <div className="col-md-8">
                  <label className="form-label small">Nome *</label>
                  <input className="form-control form-control-sm" value={formItem.nmItem}
                    onChange={(e) => setFormItem((p) => ({ ...p, nmItem: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Categoria</label>
                  <select className="form-select form-select-sm" value={formItem.categoria}
                    onChange={(e) => {
                      const categoria = e.target.value;
                      setFormItem((p) => {
                        const sug = aplicarSugestaoCategoria(categoria, unidades, p);
                        return { ...p, categoria, ...sug };
                      });
                    }}>
                    {(meta.categorias || ["Outro"]).map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
                  </select>
                  {SUGESTAO_UM_CATEGORIA[formItem.categoria]?.operacional && (
                    <div className="form-text">
                      Sugestão: {SUGESTAO_UM_CATEGORIA[formItem.categoria].basePreferida || "base"}
                      {" + "}{SUGESTAO_UM_CATEGORIA[formItem.categoria].operacional} (saída)
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Unidade base (estoque / CM) *</label>
                  <select className="form-select form-select-sm" value={formItem.idUnidadeBase}
                    onChange={(e) => setFormItem((p) => ({
                      ...p, idUnidadeBase: e.target.value, idUnidadeOperacional: "",
                    }))}>
                    <option value="">Selecione…</option>
                    {unidadesBase(unidades).map((u) => (
                      <option key={u.id} value={u.id}>{u.sigla} — {u.descricao}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Unidade de saída (opcional)</label>
                  <select className="form-select form-select-sm" value={formItem.idUnidadeOperacional}
                    onChange={(e) => setFormItem((p) => ({ ...p, idUnidadeOperacional: e.target.value }))}
                    disabled={!formItem.idUnidadeBase}>
                    <option value="">Mesma da base</option>
                    {unidadesOperacionais(unidades, formItem.idUnidadeBase)
                      .filter((u) => !u.ehBase)
                      .map((u) => (
                        <option key={u.id} value={u.id}>{u.sigla} (×{u.fatorParaBase} → base)</option>
                      ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">UM de compra / NF (opcional)</label>
                  <select className="form-select form-select-sm" value={formItem.idUnidadeCompra}
                    onChange={(e) => setFormItem((p) => ({ ...p, idUnidadeCompra: e.target.value }))}
                    disabled={!formItem.idUnidadeBase}>
                    <option value="">Mesma da base</option>
                    {unidadesOperacionais(unidades, formItem.idUnidadeBase).map((u) => (
                      <option key={u.id} value={u.id}>{u.sigla} (×{u.fatorParaBase} → base)</option>
                    ))}
                  </select>
                  <div className="form-text">Ex.: CX, saco ou ML na nota fiscal.</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Estoque mínimo</label>
                  <input type="number" step="0.001" className="form-control form-control-sm" value={formItem.estoqueMinimo}
                    onChange={(e) => setFormItem((p) => ({ ...p, estoqueMinimo: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Local padrão</label>
                  <select className="form-select form-select-sm" value={formItem.idLocalPadrao}
                    onChange={(e) => setFormItem((p) => ({ ...p, idLocalPadrao: e.target.value }))}>
                    <option value="">—</option>
                    {locais.filter((l) => l.ativo).map((l) => (
                      <option key={l.id} value={l.id}>{localNome(l)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Fornecedor preferencial</label>
                  <select className="form-select form-select-sm" value={formItem.idFornecedor}
                    onChange={(e) => setFormItem((p) => ({ ...p, idFornecedor: e.target.value }))}>
                    <option value="">—</option>
                    {fornecedores.map((f) => (
                      <option key={f.id} value={f.id}>{f.nmReduzido || f.nmRazaoSocial}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small">Observação</label>
                  <textarea className="form-control form-control-sm" rows={2} value={formItem.observacao}
                    onChange={(e) => setFormItem((p) => ({ ...p, observacao: e.target.value }))} />
                </div>
                {editItem && (
                  <div className="col-12">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" checked={!!formItem.ativo}
                        onChange={(e) => setFormItem((p) => ({ ...p, ativo: e.target.checked ? 1 : 0 }))} />
                      <label className="form-check-label small">Ativo</label>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowItem(false)}>Cancelar</button>
                {canEdit && (
                  <button type="button" className="btn btn-primary" disabled={saving || !formItem.nmItem.trim() || !formItem.idUnidadeBase} onClick={salvarItem}>
                    {saving ? "Salvando…" : "Salvar"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Local */}
      {showLocal && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editLocal ? "Editar local" : "Novo local"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowLocal(false)} />
              </div>
              <div className="modal-body">
                <label className="form-label small">Nome *</label>
                <input className="form-control form-control-sm mb-2" value={formLocal.nmLocal}
                  onChange={(e) => setFormLocal((p) => ({ ...p, nmLocal: e.target.value }))} />
                <label className="form-label small">Descrição</label>
                <textarea className="form-control form-control-sm mb-2" rows={2} value={formLocal.descricao}
                  onChange={(e) => setFormLocal((p) => ({ ...p, descricao: e.target.value }))} />
                {editLocal && (
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={!!formLocal.ativo}
                      onChange={(e) => setFormLocal((p) => ({ ...p, ativo: e.target.checked ? 1 : 0 }))} />
                    <label className="form-check-label small">Ativo</label>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowLocal(false)}>Cancelar</button>
                {canEdit && (
                  <button type="button" className="btn btn-primary" disabled={saving || !formLocal.nmLocal.trim()} onClick={salvarLocal}>
                    {saving ? "Salvando…" : "Salvar"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Movimentação */}
      {showMov && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nova movimentação</h5>
                <button type="button" className="btn-close" onClick={() => setShowMov(false)} />
              </div>
              <div className="modal-body row g-2">
                <div className="col-6">
                  <label className="form-label small">Tipo</label>
                  <select className="form-select form-select-sm" value={formMov.tpMovimento}
                    onChange={(e) => {
                      const tp = e.target.value;
                      const it = itensSelect.find((i) => String(i.id) === String(formMov.idItem));
                      let idUm = itemIdUmBase(it) || "";
                      if (tp === "Saida") {
                        const op = itemIdUmOp(it);
                        if (op) idUm = op;
                      }
                      setFormMov((p) => ({
                        ...p, tpMovimento: tp, idLocalDestino: "", idUnidadeInformada: idUm, quantidadeInformada: "",
                      }));
                    }}>
                    {(meta.tpMovimento || ["Entrada", "Saida", "Transferencia"]).map((t) => (
                      <option key={t} value={t}>{MOV_LABEL[t] || t}</option>
                    ))}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label small">Data</label>
                  <input type="date" className="form-control form-control-sm" value={formMov.dtMovimento}
                    onChange={(e) => setFormMov((p) => ({ ...p, dtMovimento: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label small">Item *</label>
                  <ItemEstoqueAutocomplete
                    listId="mov-item-estoque-list"
                    itens={itensSelect}
                    value={formMov.idItem}
                    onChange={aoSelecionarItemMov}
                    placeholder="Digite código ou descrição do produto…"
                  />
                  <div className="form-text">Busque pelo código ou pelo nome do item.</div>
                </div>
                <div className="col-12">
                  <label className="form-label small">
                    {formMov.tpMovimento === "Transferencia" ? "Local origem *" : "Local *"}
                  </label>
                  <select className="form-select form-select-sm" value={formMov.idLocalOrigem}
                    onChange={(e) => setFormMov((p) => ({ ...p, idLocalOrigem: e.target.value }))}>
                    <option value="">Selecione…</option>
                    {locais.filter((l) => l.ativo).map((l) => (
                      <option key={l.id} value={l.id}>{localNome(l)}</option>
                    ))}
                  </select>
                </div>
                {formMov.tpMovimento === "Transferencia" && (
                  <div className="col-12">
                    <label className="form-label small">Local destino *</label>
                    <select className="form-select form-select-sm" value={formMov.idLocalDestino}
                      onChange={(e) => setFormMov((p) => ({ ...p, idLocalDestino: e.target.value }))}>
                      <option value="">Selecione…</option>
                      {locais.filter((l) => l.ativo && String(l.id) !== String(formMov.idLocalOrigem)).map((l) => (
                        <option key={l.id} value={l.id}>{localNome(l)}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-4">
                  <label className="form-label small">Quantidade *</label>
                  <input type="number" step="0.001" className="form-control form-control-sm"
                    value={formMov.quantidadeInformada}
                    onChange={(e) => setFormMov((p) => ({ ...p, quantidadeInformada: e.target.value }))} />
                </div>
                <div className="col-4">
                  <label className="form-label small">Unidade</label>
                  <select className="form-select form-select-sm" value={formMov.idUnidadeInformada}
                    onChange={(e) => setFormMov((p) => ({ ...p, idUnidadeInformada: e.target.value }))}
                    disabled={!formMov.idItem}>
                    <option value="">—</option>
                    {(formMov.idItem
                      ? unidadesOperacionais(unidades, itemIdUmBase(itensSelect.find((i) => String(i.id) === String(formMov.idItem))))
                      : unidades.filter((u) => u.ativo)
                    ).map((u) => (
                      <option key={u.id} value={u.id}>{u.sigla}</option>
                    ))}
                  </select>
                </div>
                <div className="col-4">
                  <label className="form-label small">Ref. documento</label>
                  <input className="form-control form-control-sm" value={formMov.documentoRef}
                    onChange={(e) => setFormMov((p) => ({ ...p, documentoRef: e.target.value }))} />
                </div>
                {qtdBasePreview != null && formMov.idUnidadeInformada && (
                  <div className="col-12">
                    <div className="alert alert-info py-2 small mb-0">
                      Equivale a <strong>{fmtQtd(qtdBasePreview)} {siglaBasePreview}</strong> na unidade base (estoque / custo médio).
                    </div>
                  </div>
                )}
                {formMov.tpMovimento === "Entrada" && (
                  <div className="col-6">
                    <label className="form-label small">Custo unit. R$/base (opcional)</label>
                    <input type="number" step="0.0001" className="form-control form-control-sm" value={formMov.vlrUnitario}
                      onChange={(e) => setFormMov((p) => ({ ...p, vlrUnitario: e.target.value }))} />
                  </div>
                )}
                <div className="col-12">
                  <label className="form-label small">Observação</label>
                  <input className="form-control form-control-sm" value={formMov.observacao}
                    onChange={(e) => setFormMov((p) => ({ ...p, observacao: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowMov(false)}>Cancelar</button>
                <button type="button" className="btn btn-primary" disabled={saving || !formMov.idItem || !formMov.quantidadeInformada || !formMov.idUnidadeInformada} onClick={salvarMov}>
                  {saving ? "Salvando…" : "Registrar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Unidade de medida */}
      {showUm && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editUm ? "Editar unidade" : "Nova unidade"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowUm(false)} />
              </div>
              <div className="modal-body">
                <label className="form-label small">Sigla *</label>
                <input className="form-control form-control-sm mb-2" value={formUm.sigla}
                  disabled={!!editUm}
                  onChange={(e) => setFormUm((p) => ({ ...p, sigla: e.target.value.toUpperCase() }))}
                  placeholder="Ex.: GR, KG" />
                <label className="form-label small">Descrição</label>
                <input className="form-control form-control-sm mb-2" value={formUm.descricao}
                  onChange={(e) => setFormUm((p) => ({ ...p, descricao: e.target.value }))} />
                <label className="form-label small">Família</label>
                <select className="form-select form-select-sm mb-2" value={formUm.familia}
                  onChange={(e) => setFormUm((p) => ({ ...p, familia: e.target.value, idUnidadeBase: "" }))}>
                  {(meta.familias || Object.keys(FAMILIA_LABEL)).map((f) => (
                    <option key={f} value={f}>{FAMILIA_LABEL[f] || f}</option>
                  ))}
                </select>
                <label className="form-label small">Unidade base (conversão)</label>
                <select className="form-select form-select-sm mb-2" value={formUm.idUnidadeBase}
                  onChange={(e) => setFormUm((p) => ({
                    ...p,
                    idUnidadeBase: e.target.value,
                    fatorParaBase: e.target.value ? p.fatorParaBase : "1",
                  }))}>
                  <option value="">Nenhuma — esta é a unidade raiz (base)</option>
                  {unidadesBase(unidades)
                    .filter((u) => u.familia === formUm.familia && (!editUm || String(u.id) !== String(editUm.id)))
                    .map((u) => (
                      <option key={u.id} value={u.id}>{u.sigla} — {u.descricao}</option>
                    ))}
                </select>
                <label className="form-label small">Fator para a base</label>
                <input type="number" step="any" className="form-control form-control-sm mb-1"
                  value={formUm.fatorParaBase}
                  disabled={!formUm.idUnidadeBase}
                  onChange={(e) => setFormUm((p) => ({ ...p, fatorParaBase: e.target.value }))} />
                <div className="form-text mb-2">
                  Ex.: 1 GR com fator 0,001 → 1 grama = 0,001 kg na base.
                </div>
                {editUm && (
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={!!formUm.ativo}
                      onChange={(e) => setFormUm((p) => ({ ...p, ativo: e.target.checked ? 1 : 0 }))} />
                    <label className="form-check-label small">Ativo</label>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowUm(false)}>Cancelar</button>
                {canEdit && (
                  <button type="button" className="btn btn-primary"
                    disabled={saving || !formUm.sigla.trim() || (formUm.idUnidadeBase && !(parseFloat(formUm.fatorParaBase) > 0))}
                    onClick={salvarUm}>
                    {saving ? "Salvando…" : "Salvar"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showKit && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editKit ? "Editar kit" : "Novo kit"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowKit(false)} />
              </div>
              <div className="modal-body row g-2">
                <div className="col-md-8">
                  <label className="form-label small">Nome do kit *</label>
                  <input className="form-control form-control-sm" value={formKit.nmKit}
                    onChange={(e) => setFormKit((p) => ({ ...p, nmKit: e.target.value }))}
                    placeholder="Ex.: Limpeza piscina semanal" />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Categoria</label>
                  <select className="form-select form-select-sm" value={formKit.categoria}
                    onChange={(e) => setFormKit((p) => ({ ...p, categoria: e.target.value }))}>
                    {(meta.categorias || ["Piscina", "Outro"]).map((c) => (
                      <option key={c} value={c}>{CAT_LABEL[c] || c}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small">Observação</label>
                  <textarea className="form-control form-control-sm" rows={2} value={formKit.observacao}
                    onChange={(e) => setFormKit((p) => ({ ...p, observacao: e.target.value }))} />
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label small mb-0">Itens do kit *</label>
                    <button type="button" className="btn btn-outline-primary btn-sm"
                      onClick={() => setFormKit((p) => ({ ...p, linhas: [...p.linhas, { ...KIT_LINHA_EMPTY }] }))}>
                      <i className="bi bi-plus-lg" /> Linha
                    </button>
                  </div>
                  {formKit.linhas.map((lin, idx) => (
                    <div key={idx} className="row g-2 align-items-end mb-2">
                      <div className="col-md-5">
                        <ItemEstoqueAutocomplete
                          listId={`kit-item-list-${idx}`}
                          itens={itensSelect}
                          value={lin.idItem}
                          placeholder="Código ou descrição…"
                          onChange={(idItem) => {
                            const it = itensSelect.find((i) => String(i.id) === String(idItem));
                            const idUm = itemIdUmOp(it) || itemIdUmBase(it) || "";
                            updLinhaKit(idx, { idItem, idUnidadeInformada: idUm ? String(idUm) : "" });
                          }}
                        />
                      </div>
                      <div className="col-3">
                        <input type="number" step="0.001" className="form-control form-control-sm"
                          placeholder="Qtd" value={lin.quantidade}
                          onChange={(e) => updLinhaKit(idx, { quantidade: e.target.value })} />
                      </div>
                      <div className="col-3">
                        <select className="form-select form-select-sm" value={lin.idUnidadeInformada}
                          onChange={(e) => updLinhaKit(idx, { idUnidadeInformada: e.target.value })}
                          disabled={!lin.idItem}>
                          <option value="">Base</option>
                          {unidadesOperacionais(
                            unidades,
                            itemIdUmBase(itensSelect.find((i) => String(i.id) === String(lin.idItem)))
                          ).map((u) => (
                            <option key={u.id} value={u.id}>{u.sigla}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-1">
                        {formKit.linhas.length > 1 && (
                          <button type="button" className="btn btn-outline-danger btn-sm w-100"
                            onClick={() => setFormKit((p) => ({
                              ...p,
                              linhas: p.linhas.filter((_, i) => i !== idx),
                            }))}>
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowKit(false)}>Cancelar</button>
                {canEdit && (
                  <button type="button" className="btn btn-primary"
                    disabled={saving || !formKit.nmKit.trim() || !kitLinhasValidas(formKit.linhas).length}
                    onClick={salvarKit}>
                    {saving ? "Salvando…" : "Salvar"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showAplicarKit && kitAplicar && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Aplicar kit — {kitAplicar.nmkit || kitAplicar.nmKit}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowAplicarKit(false)} />
              </div>
              <div className="modal-body row g-2">
                <div className="col-md-6">
                  <label className="form-label small">Local de saída *</label>
                  <select className="form-select form-select-sm" value={formAplicarKit.idLocalOrigem}
                    onChange={(e) => setFormAplicarKit((p) => ({ ...p, idLocalOrigem: e.target.value }))}>
                    <option value="">Selecione…</option>
                    {locais.filter((l) => l.ativo).map((l) => (
                      <option key={l.id} value={l.id}>{localNome(l)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Observação (opcional)</label>
                  <input className="form-control form-control-sm" value={formAplicarKit.observacao}
                    onChange={(e) => setFormAplicarKit((p) => ({ ...p, observacao: e.target.value }))} />
                </div>
                <div className="col-12">
                  <p className="small text-muted mb-1">Ajuste as quantidades se necessário antes de confirmar.</p>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr><th>Item</th><th style={{ width: 120 }}>Qtd</th><th style={{ width: 100 }}>UM</th></tr>
                      </thead>
                      <tbody>
                        {formAplicarKit.linhas.map((lin, idx) => (
                          <tr key={lin.id || idx}>
                            <td>
                              <code className="small">{lin.cditem}</code> {lin.nmitem}
                            </td>
                            <td>
                              <input type="number" step="0.001" className="form-control form-control-sm"
                                value={lin.quantidade}
                                onChange={(e) => updLinhaAplicar(idx, { quantidade: e.target.value })} />
                            </td>
                            <td>
                              <select className="form-select form-select-sm" value={lin.idUnidadeInformada}
                                onChange={(e) => updLinhaAplicar(idx, { idUnidadeInformada: e.target.value })}
                                disabled={!lin.idItem}>
                                <option value="">Base</option>
                                {unidadesOperacionais(
                                  unidades,
                                  itemIdUmBase(itensSelect.find((i) => String(i.id) === String(lin.idItem)))
                                ).map((u) => (
                                  <option key={u.id} value={u.id}>{u.sigla}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAplicarKit(false)}>Cancelar</button>
                {canEdit && (
                  <button type="button" className="btn btn-success"
                    disabled={saving || !formAplicarKit.idLocalOrigem || !formAplicarKit.linhas.length}
                    onClick={confirmarAplicarKit}>
                    {saving ? "Aplicando…" : "Confirmar saídas"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
